import { useState, type FormEvent } from 'react';
import { toUserMessage } from '@platform/api-client';

import { Alert, FormField } from '@/shared/ui';
import {
  useOnboardingEmail,
  useOnboardingEmailVerify,
} from '../../api/use-auth.mutation';
import { EMAIL_OTP_TTL_SECONDS } from '../../constants/cooldown.constant';
import { emailChangeSchema } from '../../schemas/auth.schemas';
import { CodeVerifier } from './CodeVerifier';
import { PrimaryButton, StepHeading } from './OnboardingUi';

/**
 * 2단계 — 메일로 받은 6자리 인증번호를 이 화면에서 입력한다.
 * 이관 때 이메일이 없던 계정(`replacementRequired`)은 먼저 사용할 주소를 받는다.
 */
export function EmailVerifyStep({
  email,
  replacementRequired,
}: {
  email: string;
  replacementRequired: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string>();
  const [sent, setSent] = useState<{ to: string; at: number } | null>(null);
  const send = useOnboardingEmail();
  const verify = useOnboardingEmailVerify();

  const requestCode = (newEmail?: string) => {
    verify.reset();
    send.mutate(newEmail, {
      onSuccess: (res) => setSent({ to: res.email, at: Date.now() }),
    });
  };

  const handleAddressSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = emailChangeSchema.shape.newEmail.safeParse(draft.trim());
    if (!parsed.success) {
      setDraftError(parsed.error.issues[0]?.message);
      return;
    }
    setDraftError(undefined);
    requestCode(parsed.data);
  };

  const sendError = send.error && (
    <Alert tone="error">{toUserMessage(send.error)}</Alert>
  );

  if (sent) {
    return (
      <div>
        <StepHeading title="인증번호를 입력해 주세요">
          <b className="break-all font-semibold text-neutral-950 dark:text-neutral-100">
            {sent.to}
          </b>
          (으)로 보낸 6자리 인증번호를 입력해 주세요. 메일이 보이지 않으면
          스팸함도 확인해 주세요.
        </StepHeading>
        <CodeVerifier
          key={sent.at}
          sentAt={sent.at}
          ttlSeconds={EMAIL_OTP_TTL_SECONDS}
          onSubmit={(code) => verify.mutate(code)}
          submitting={verify.isPending}
          submitLabel="확인"
          error={verify.error ? toUserMessage(verify.error) : undefined}
          onResend={() => requestCode()}
          resending={send.isPending}
        />
        {sendError && <div className="mt-3">{sendError}</div>}
      </div>
    );
  }

  if (replacementRequired) {
    return (
      <form onSubmit={handleAddressSubmit} className="space-y-5" noValidate>
        <StepHeading title="이메일을 등록해 주세요">
          등록된 이메일이 없습니다. 사용할 이메일을 입력하면 인증번호를 보내
          드립니다.
        </StepHeading>
        <FormField
          label="이메일"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          required
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setDraftError(undefined);
            if (send.error) send.reset();
          }}
          error={draftError}
        />
        {sendError}
        <PrimaryButton type="submit" disabled={send.isPending}>
          {send.isPending ? '보내는 중…' : '인증번호 받기'}
        </PrimaryButton>
      </form>
    );
  }

  return (
    <div className="space-y-5">
      <StepHeading title="이메일을 인증해 주세요">
        아래 주소로 6자리 인증번호를 보내 드립니다.
      </StepHeading>
      <p className="break-all rounded-lg bg-neutral-100 px-4 py-3 text-sm font-semibold text-neutral-950 dark:bg-white/5 dark:text-neutral-100">
        {email}
      </p>
      {sendError}
      <PrimaryButton onClick={() => requestCode()} disabled={send.isPending}>
        {send.isPending ? '보내는 중…' : '인증번호 받기'}
      </PrimaryButton>
      <p className="break-keep text-xs text-neutral-600 dark:text-neutral-400">
        주소가 잘못되었다면 소속 기업의 관리자(기업 회원) 또는 고객센터(개인
        회원)에 문의해 주세요.
      </p>
    </div>
  );
}
